/**
 * «Что показывать про соединение прямо сейчас» — состояние нити под шапкой.
 *
 * Отдельный файл, а не логика внутри компонента: у нити четыре шапки-хоста (общая шапка админки
 * и кабинета плюс три собственные шапки кабинета на телефоне), и правило «когда показывать»
 * должно быть одно на всех и покрыто тестами с фальшивыми таймерами.
 *
 * Главное здесь — ЗАДЕРЖКА. `signalr.ts` держит `RECONNECT_DELAYS = [0, 2000, 5000, …]`, то есть
 * первая попытка переподключиться уходит мгновенно, а вторая через две секунды. Прежний баннер
 * поднимался на первом же `Reconnecting` и поэтому вспыхивал на каждом микро-разрыве — сдвигая
 * заодно всю страницу. Молчим четыре секунды: две первые попытки успевают отработать незаметно,
 * а разрыв, переживший их обе, — уже настоящий. Эта же задержка закрывает и первую загрузку
 * страницы, ради которой прежний баннер завёл флаг, но так его и не проверил: до конца отсчёта
 * состояние успевает уйти из стартового `Disconnected` в `Connected`.
 *
 * Из той же логики следует и второе: «Соединение восстановлено» показывается ТОЛЬКО если нить
 * успела появиться. Обрыв, о котором никто не узнал, не нуждается в отбое.
 *
 * Отсчёт НЕ перезапускается на каждой смене состояния внутри разрыва: `Reconnecting` и
 * `Disconnected` сменяют друг друга столько раз, сколько попыток сделает менеджер, и таймер,
 * привязанный к очистке эффекта, не дожил бы до срабатывания ни разу.
 */

import { useEffect, useRef, useState } from 'react';
import { useSignalRState } from './useNotifications';

/** Сколько молчим, прежде чем признать разрыв разрывом. */
export const SHOW_AFTER_MS = 4000;

/** Сколько держится зелёная нить отбоя. */
export const RESTORED_MS = 1200;

export type ConnectionIndicatorState = 'idle' | 'lost' | 'restored';

/**
 * @example
 * ```tsx
 * const state = useConnectionIndicator();
 * return <ConnectionThread state={state} />;
 * ```
 */
export function useConnectionIndicator(): ConnectionIndicatorState {
  const { connectionState, isConnected } = useSignalRState();
  const [state, setState] = useState<ConnectionIndicatorState>('idle');

  /** Успела ли нить показаться в этом разрыве — от этого зависит, будет ли отбой. */
  const announcedRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * Чей таймер сейчас висит. Одного «висит ли» не хватает: пока идёт отбой, таймер есть, и
   * ветка разрыва выходила бы по нему раньше, чем успела погасить зелёную нить.
   */
  const pendingRef = useRef<'none' | 'lost' | 'restored'>('none');

  useEffect(() => {
    const clear = (): void => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      pendingRef.current = 'none';
    };

    if (connectionState === 'Reconnecting' || connectionState === 'Disconnected') {
      // Нить уже показана, или отсчёт до неё уже идёт — второй раз не начинаем.
      if (announcedRef.current || pendingRef.current === 'lost') return;

      // Порвалось прямо во время отбоя: зелёная нить обязана уйти сразу, а не досидеть свою
      // секунду поверх нового разрыва.
      if (pendingRef.current === 'restored') {
        clear();
        setState('idle');
      }

      pendingRef.current = 'lost';
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        pendingRef.current = 'none';
        announcedRef.current = true;
        setState('lost');
      }, SHOW_AFTER_MS);
      return;
    }

    // `Connecting` и `Disconnecting` — промежуточные: не разрыв и ещё не связь, ничего не трогаем.
    if (!isConnected) return;

    clear();

    if (announcedRef.current) {
      announcedRef.current = false;
      pendingRef.current = 'restored';
      setState('restored');
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        pendingRef.current = 'none';
        setState('idle');
      }, RESTORED_MS);
    }
  }, [connectionState, isConnected]);

  // Единственная очистка — на размонтировании. Внутри разрыва таймер должен пережить
  // перезапуск эффекта, поэтому очистка не может висеть на нём.
  useEffect(
    () => () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    },
    []
  );

  return state;
}

export default useConnectionIndicator;
